import { isFileUIPart, type UIMessage } from 'ai';
import { getAssistantDisplayText } from './assistantMessageContent';

export type BagelApiChatMessage = {
  role: 'user' | 'assistant';
  content: string;
};

type MappableChatMessage = {
  role: UIMessage['role'];
  parts?: UIMessage['parts'];
  content?: string;
};

// A real, already-uploaded asset URL surfaced as plain text so the model can reference it
// verbatim in code — getAssistantDisplayText is display-only and drops non-text parts, which
// silently dropped image uploads from the API request entirely (a real production bug: a
// message with only a file part and no text collapsed to '' and got filtered out below before
// ever reaching the backend). Mirrors bagel-chat-messages.util.ts on the backend.
function describeFileParts(parts: UIMessage['parts'] | undefined): string {
  if (!Array.isArray(parts)) return '';
  return parts
    .filter(isFileUIPart)
    .map(part => {
      const label = part.filename ? ` (${part.filename})` : '';
      return `[Uploaded image${label} — url: ${part.url}]`;
    })
    .join('\n\n');
}

/**
 * The one place a UIMessage becomes plain `{ role, content }` text — used both for the live API
 * request (toBagelApiMessages) and for session persistence (toPersistableMessages in
 * BagelChatContext). A second, independent flattening path used to exist only for persistence
 * (getAssistantDisplayText direct) and never got this file-part handling — a real bug: an
 * uploaded image's URL survived the live turn but was silently dropped the moment the session was
 * saved, so it vanished from the model's context (and the UI) after any refresh. One function now,
 * shared by both callers, so this can't drift out of sync again.
 */
export function toApiMessageContent(message: MappableChatMessage): string {
  const fileText = describeFileParts(message.parts);
  const text = getAssistantDisplayText(message);
  return [fileText, text].filter(Boolean).join('\n\n');
}

/**
 * Map AI SDK UI messages (`parts`) to the `{ role, content }` shape the
 * Bagel chat API expects.
 */
export function toBagelApiMessages(
  messages: MappableChatMessage[],
): BagelApiChatMessage[] {
  return messages
    .filter(
      (message): message is MappableChatMessage & { role: 'user' | 'assistant' } =>
        message.role === 'user' || message.role === 'assistant',
    )
    .map(message => ({ role: message.role, content: toApiMessageContent(message) }))
    .filter(message => message.content.trim());
}

const UPLOADED_IMAGE_ANNOTATION_RE =
  /^\[Uploaded image(?: \(([^)]+)\))? — url: (\S+)\]$/;

/**
 * Reverses toApiMessageContent's file annotation back into a real `file` part when restoring a
 * persisted session — so an uploaded image keeps rendering as a thumbnail (not literal bracket
 * text) and keeps being a real, re-describable attachment on the next turn, across a refresh.
 */
export function parsePersistedMessageParts(
  content: string,
): Array<{ type: 'file'; url: string; filename?: string; mediaType: string } | { type: 'text'; text: string }> {
  const segments = content.split('\n\n');
  const matches = segments.map(segment => ({
    segment,
    fileMatch: segment.match(UPLOADED_IMAGE_ANNOTATION_RE),
  }));

  const fileParts = matches
    .filter(({ fileMatch }) => fileMatch)
    .map(({ fileMatch }) => ({
      type: 'file' as const,
      url: fileMatch![2],
      filename: fileMatch![1],
      mediaType: 'image/*',
    }));

  const remainingText = matches
    .filter(({ fileMatch, segment }) => !fileMatch && segment.trim())
    .map(({ segment }) => segment)
    .join('\n\n');

  return remainingText ? [...fileParts, { type: 'text' as const, text: remainingText }] : fileParts;
}
